import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import McpDocumentation from './McpDocumentation';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  Plug, Plus, Copy, Check, Loader2, RotateCw, Ban, Globe, Building2, ArrowLeft,
  ScrollText, ShieldAlert, Eye, Search, X, Settings, KeyRound, Sparkles, MessageSquare,
} from 'lucide-react';

const API = '/api';

const STATUS_CLS = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  revoked: 'bg-red-50 text-red-700 border-red-200',
  expired: 'bg-slate-100 text-slate-600 border-slate-200',
};
const RESULT_CLS = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  blocked: 'bg-amber-50 text-amber-800 border-amber-300',
  error: 'bg-red-50 text-red-700 border-red-200',
};
const RESULT_LABEL = { success: 'Success', blocked: 'Blocked', error: 'Error' };

// Integrations shown as "Coming Soon" in the picker regardless of backend state.
// Add future keys here to make them non-selectable without redesigning the page.
const COMING_SOON = new Set(['microsoft_365', 'trust_center', 'device_trust']);

const TRY_PROMPTS = [
  'What is our compliance posture?',
  'Show my biggest SOC 2 gaps.',
  'Which evidence is stale?',
  'Show GitHub integration status.',
  'Run Google Workspace evidence collection.',
];

// ---- formatting helpers (UTC stored, viewer-local displayed) -------------
// `tz` is accepted now so a future workspace timezone can be passed straight through.
const _d = (iso) => { if (!iso) return null; const d = new Date(iso); return isNaN(d.getTime()) ? null : d; };
const fmtLog = (iso, tz) => {
  const d = _d(iso); if (!d) return '—';
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: tz || undefined });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: tz || undefined });
  return `${date} • ${time}`;
};
const fmtFull = (iso, tz) => {
  const d = _d(iso); if (!d) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz || undefined });
};
const fmtLatency = (ms) => {
  if (ms === null || ms === undefined || ms === '') return '—';
  const n = Number(ms);
  if (Number.isNaN(n)) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${n} ms`;
};
const truncId = (id) => (id && id.length > 10 ? `${id.slice(0, 4)}…${id.slice(-4)}` : (id || '—'));

const Pill = ({ text, cls, testId, title }) => (
  <span data-testid={testId} title={title} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{text}</span>
);
const CapBadges = ({ caps }) => (
  <span className="flex gap-1 flex-wrap">
    {(caps || []).map((c) => (
      <span key={c} className={`text-[10px] px-1.5 py-0.5 rounded border ${c === 'global' ? 'bg-purple-50 text-purple-700 border-purple-200' : c === 'write' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-sky-50 text-sky-700 border-sky-200'}`}>{c}</span>
    ))}
  </span>
);
// nova_mcp_4Hd9••••••••••8FxA — enough to identify, never the full token.
const MaskedToken = ({ value }) => {
  if (!value || value === '—') return <span className="text-black/30">—</span>;
  const [pre, last] = String(value).split('…');
  return (
    <span className="font-mono text-[11px] whitespace-nowrap">{pre}<span className="text-black/25 tracking-tight">••••••••••</span>{last || ''}</span>
  );
};
const CopyRequestId = ({ id }) => {
  const [done, setDone] = useState(false);
  if (!id) return <span className="text-black/30">—</span>;
  const copy = () => navigator.clipboard.writeText(id).then(() => { setDone(true); setTimeout(() => setDone(false), 1200); }).catch(() => {});
  return (
    <button onClick={copy} title={`${id}\n(click to copy)`} className="font-mono text-[11px] text-black/45 hover:text-[#0F62FE] inline-flex items-center gap-1">
      {truncId(id)} {done ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100" />}
    </button>
  );
};

export default function SuperAdminMcp() {
  const navigate = useNavigate();
  const [conns, setConns] = useState([]);
  const [logs, setLogs] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [viewConn, setViewConn] = useState(null);
  const [copiedPrompt, setCopiedPrompt] = useState(null);
  const [logFilter, setLogFilter] = useState({ rw: 'all', result: 'all' });
  const [form, setForm] = useState({ name: '', scope: 'selected', workspace_ids: [], sources: [], write: false, ttl_days: 30 });
  const [wsSearch, setWsSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const [c, w, s] = await Promise.all([
        axios.get(`${API}/admin/mcp/connections`),
        axios.get(`${API}/admin/mcp/workspaces`),
        axios.get(`${API}/admin/mcp/sources`),
      ]);
      setConns(c.data.connections || []);
      setWorkspaces(w.data.workspaces || []);
      setSources(s.data.sources || []);
    } catch { toast.error('Failed to load MCP settings'); }
    finally { setLoading(false); }
  }, []);
  const loadLogs = useCallback(async () => {
    const p = new URLSearchParams();
    if (logFilter.rw !== 'all') p.set('rw', logFilter.rw);
    if (logFilter.result !== 'all') p.set('result', logFilter.result);
    try { const r = await axios.get(`${API}/admin/mcp/logs?${p.toString()}`); setLogs(r.data.logs || []); } catch { /* */ }
  }, [logFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const wsById = (id) => workspaces.find((w) => w.id === id);
  const wsName = (id) => (wsById(id) || {}).name || (id ? id.slice(0, 8) + '…' : '—');
  const wsConcise = (ids) => {
    if (!ids || ids.length === 0) return 'All (global)';
    const first = wsName(ids[0]);
    return ids.length > 1 ? `${first} +${ids.length - 1}` : first;
  };
  const connName = (keyId) => (conns.find((c) => c.id === keyId) || {}).name || '—';
  // Derive "Last Successful Request" per connection from the loaded logs (best-effort for demo).
  const lastSuccessFor = (keyId) => {
    const hit = logs.find((l) => l.key_id === keyId && (l.result === 'success' || (l.ok && !l.result)));
    return hit ? hit.ts : null;
  };

  const filteredWs = workspaces.filter((w) => {
    const q = wsSearch.trim().toLowerCase();
    if (!q) return true;
    return [w.name, w.id, w.company_name, w.owner_email].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });
  const toggleWs = (id) => setForm((f) => ({ ...f, workspace_ids: f.workspace_ids.includes(id) ? f.workspace_ids.filter((x) => x !== id) : [...f.workspace_ids, id] }));
  const toggleSource = (k) => setForm((f) => ({ ...f, sources: f.sources.includes(k) ? f.sources.filter((x) => x !== k) : [...f.sources, k] }));
  const selectableSources = useMemo(() => sources.map((s) => ({ ...s, comingSoon: !s.available || COMING_SOON.has(s.key) })), [sources]);

  const create = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (form.scope === 'selected' && form.workspace_ids.length === 0) return toast.error('Select at least one workspace');
    setCreating(true);
    try {
      const perms = form.scope === 'global' ? (form.write ? ['read', 'write', 'global'] : ['read', 'global']) : (form.write ? ['read', 'write'] : ['read']);
      const r = await axios.post(`${API}/admin/mcp/connections`, {
        name: form.name, scope: form.scope,
        workspace_ids: form.scope === 'selected' ? form.workspace_ids : [],
        sources: form.sources, permissions: perms, ttl_days: Number(form.ttl_days) || 30,
      });
      setCreated(r.data); toast.success('MCP connection created'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create'); }
    finally { setCreating(false); }
  };
  const rotate = async (id) => {
    try { const r = await axios.post(`${API}/admin/mcp/connections/${id}/rotate`); setViewConn(null); setCreated(r.data); setOpen(true); toast.success('Rotated — new credential issued'); load(); }
    catch { toast.error('Rotate failed'); }
  };
  const revoke = async (id) => {
    if (!window.confirm('Revoke this connection? The credential stops working immediately.')) return;
    try { await axios.post(`${API}/admin/mcp/connections/${id}/revoke`); setViewConn(null); toast.success('Revoked'); load(); } catch { toast.error('Revoke failed'); }
  };
  const clip = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; }
    catch {
      try { const t = document.createElement('textarea'); t.value = text; t.style.position = 'fixed'; t.style.opacity = '0'; document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t); return true; }
      catch { return false; }
    }
  };
  const copyUrl = async () => { if (created && await clip(created.connector_url)) { setCopied(true); setTimeout(() => setCopied(false), 1500); } };
  const copyPrompt = async (p) => { if (await clip(p)) { setCopiedPrompt(p); setTimeout(() => setCopiedPrompt(null), 1400); } };
  const closeDialog = () => { setOpen(false); setCreated(null); setWsSearch(''); setForm({ name: '', scope: 'selected', workspace_ids: [], sources: [], write: false, ttl_days: 30 }); };

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="mcp-admin-page">
        <button onClick={() => navigate('/admin')} className="inline-flex items-center gap-1.5 text-[13px] text-black/50 hover:text-[#0F62FE] mb-3"><ArrowLeft className="w-3.5 h-3.5" /> Back to Admin</button>
        <div className="flex items-center gap-2 text-[12px] text-black/40 mb-1"><Settings className="w-3.5 h-3.5" /> Settings <span className="text-black/25">/</span> <span className="text-black/70">MCP</span></div>
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-[#0A0A0A] dark:text-[#F5F5F5] flex items-center gap-2"><Plug className="w-6 h-6 text-[#0F62FE]" /> MCP Connections</h1>
            <p className="text-sm text-black/55 dark:text-white/55 mt-1">Securely connect ChatGPT or another MCP client to Nova. Manage accessible workspaces, capabilities and request logs.</p>
          </div>
          <Button data-testid="new-mcp-connection-btn" onClick={() => { setCreated(null); setOpen(true); }} className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white"><Plus className="w-4 h-4 mr-1.5" /> New Connection</Button>
        </div>

        <Tabs defaultValue="connections">
          <TabsList data-testid="mcp-tabs"><TabsTrigger value="connections" data-testid="tab-connections">MCP Connections</TabsTrigger><TabsTrigger value="logs" data-testid="tab-logs">Request Logs</TabsTrigger><TabsTrigger value="docs" data-testid="tab-docs">Documentation</TabsTrigger></TabsList>

          <TabsContent value="connections" className="mt-4 space-y-4">
            <Card className="border-black/10"><CardContent className="p-0">
              {loading ? <div className="p-10 flex items-center justify-center text-black/40"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
                : conns.length === 0 ? (
                  <div className="p-12 text-center" data-testid="mcp-connections-empty">
                    <div className="mx-auto w-11 h-11 rounded-full bg-[#0F62FE]/10 flex items-center justify-center mb-3"><Plug className="w-5 h-5 text-[#0F62FE]" /></div>
                    <p className="text-[15px] font-semibold text-[#0A0A0A]">No MCP connections yet.</p>
                    <p className="text-[13px] text-black/50 mt-1 max-w-md mx-auto">Create your first MCP connection to securely connect ChatGPT or another MCP client to Nova.</p>
                    <Button data-testid="empty-new-connection-btn" onClick={() => { setCreated(null); setOpen(true); }} className="mt-4 bg-[#0F62FE] hover:bg-[#0A4FCC] text-white"><Plus className="w-4 h-4 mr-1.5" /> New Connection</Button>
                  </div>)
                  : (<div className="overflow-x-auto" data-testid="mcp-connections-table"><table className="w-full text-left text-[13px]">
                    <thead><tr className="text-[10px] uppercase tracking-wide text-black/40 border-b border-black/8">
                      {['Name', 'Token', 'Scope', 'Workspaces', 'Integrations', 'Capabilities', 'Status', 'Reqs', 'Last used', 'Expires', ''].map((h) => <th key={h} className="font-medium px-3 py-2.5">{h}</th>)}</tr></thead>
                    <tbody>{conns.map((c) => (
                      <tr key={c.id} className="border-b border-black/5 hover:bg-black/[0.015] group" data-testid={`mcp-conn-${c.id}`}>
                        <td className="px-3 py-2.5 font-medium">{c.name}</td>
                        <td className="px-3 py-2.5"><MaskedToken value={c.masked_token} /></td>
                        <td className="px-3 py-2.5"><span className={`inline-flex items-center gap-1 text-[11px] ${c.scope === 'global' ? 'text-purple-700' : 'text-black/60'}`}>{c.scope === 'global' ? <Globe className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}{c.scope}</span></td>
                        <td className="px-3 py-2.5 text-black/60" title={(c.allowed_workspace_ids || []).map(wsName).join(', ')}>{wsConcise(c.allowed_workspace_ids)}</td>
                        <td className="px-3 py-2.5 text-[11px] text-black/50">{(c.sources && c.sources.length) ? `${c.sources.length} selected` : 'All'}</td>
                        <td className="px-3 py-2.5"><CapBadges caps={c.permissions} /></td>
                        <td className="px-3 py-2.5"><Pill text={c.status} cls={STATUS_CLS[c.status]} testId={`mcp-status-${c.id}`} /></td>
                        <td className="px-3 py-2.5 text-black/60">{c.request_count || 0}</td>
                        <td className="px-3 py-2.5 text-[11px] text-black/50">{fmtLog(c.last_used_at)}</td>
                        <td className="px-3 py-2.5 text-[11px] text-black/50">{c.expires_at ? fmtLog(c.expires_at) : '—'}</td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap">
                          <button onClick={() => setViewConn(c)} data-testid={`view-${c.id}`} title="View" className="text-black/40 hover:text-[#0F62FE] mr-3"><Eye className="w-3.5 h-3.5 inline" /></button>
                          {c.status === 'active' && (<>
                            <button onClick={() => rotate(c.id)} data-testid={`rotate-${c.id}`} title="Rotate" className="text-black/40 hover:text-[#0F62FE] mr-3"><RotateCw className="w-3.5 h-3.5 inline" /></button>
                            <button onClick={() => revoke(c.id)} data-testid={`revoke-${c.id}`} title="Revoke" className="text-black/40 hover:text-red-600"><Ban className="w-3.5 h-3.5 inline" /></button></>)}
                        </td>
                      </tr>))}</tbody></table></div>)}
            </CardContent></Card>

            {/* Try in ChatGPT */}
            <Card className="border-[#0F62FE]/20 bg-[#0F62FE]/[0.03]" data-testid="try-in-chatgpt-card"><CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-[#0F62FE]" />
                <h2 className="text-sm font-semibold text-[#0A0A0A]">Try in ChatGPT</h2>
              </div>
              <p className="text-[12px] text-black/50 mb-3">Once a connection is added to ChatGPT, try one of these prompts. Click to copy.</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {TRY_PROMPTS.map((p, i) => (
                  <button key={p} data-testid={`try-prompt-${i}`} onClick={() => copyPrompt(p)} title="Click to copy"
                    className="group flex items-center gap-2 text-left rounded-lg border border-black/10 bg-white px-3 py-2 text-[13px] text-black/75 hover:border-[#0F62FE]/40 hover:bg-[#0F62FE]/[0.04] transition-colors">
                    <MessageSquare className="w-3.5 h-3.5 text-[#0F62FE]/70 shrink-0" />
                    <span className="flex-1">{p}</span>
                    {copiedPrompt === p ? <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <Copy className="w-3.5 h-3.5 text-black/25 group-hover:text-[#0F62FE] shrink-0" />}
                  </button>
                ))}
              </div>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="logs" className="mt-4">
            <Card className="border-black/10"><CardContent className="p-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h2 className="text-sm font-semibold flex items-center gap-2"><ScrollText className="w-4 h-4 text-black/50" /> Request logs</h2>
                <div className="flex items-center gap-2">
                  <Select value={logFilter.rw} onValueChange={(v) => setLogFilter({ ...logFilter, rw: v })}><SelectTrigger className="h-8 w-28 text-[12px]" data-testid="log-rw-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All ops</SelectItem><SelectItem value="read">Read</SelectItem><SelectItem value="write">Write</SelectItem></SelectContent></Select>
                  <Select value={logFilter.result} onValueChange={(v) => setLogFilter({ ...logFilter, result: v })}><SelectTrigger className="h-8 w-32 text-[12px]" data-testid="log-result-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All results</SelectItem><SelectItem value="success">Success</SelectItem><SelectItem value="blocked">Blocked</SelectItem><SelectItem value="error">Error</SelectItem></SelectContent></Select>
                </div>
              </div>
              {logs.length === 0 ? (
                <div className="py-12 text-center" data-testid="mcp-logs-empty">
                  <div className="mx-auto w-11 h-11 rounded-full bg-black/5 flex items-center justify-center mb-3"><ScrollText className="w-5 h-5 text-black/35" /></div>
                  <p className="text-[15px] font-semibold text-[#0A0A0A]">No MCP requests yet.</p>
                  <p className="text-[13px] text-black/50 mt-1 max-w-md mx-auto">Once an MCP connection is used, requests will appear here in real time.</p>
                </div>) : (
                <div className="overflow-x-auto" data-testid="mcp-logs-table"><table className="w-full text-left text-[12px]">
                  <thead><tr className="text-[10px] uppercase tracking-wide text-black/40 border-b border-black/8">{['Time', 'Connection', 'Tool', 'Op', 'Scope', 'Workspace', 'Result', 'Latency', 'Request ID'].map((h) => <th key={h} className="font-medium px-3 py-2">{h}</th>)}</tr></thead>
                  <tbody>{logs.map((l, i) => {
                    const res = l.result || (l.ok ? 'success' : 'error');
                    const blockedTip = res === 'blocked' ? (l.reason ? `Blocked: ${l.reason}` : 'This request was blocked because the connection lacked the required workspace scope, capability, or integration access.') : undefined;
                    return (
                    <tr key={l.request_id || `${l.ts}-${i}`} className={`group border-b border-black/5 ${res === 'blocked' ? 'bg-amber-50/40' : ''}`}>
                      <td className="px-3 py-2 text-black/60 whitespace-nowrap" title={fmtFull(l.ts)}>{fmtLog(l.ts)}</td>
                      <td className="px-3 py-2 text-black/70">{connName(l.key_id)}</td>
                      <td className="px-3 py-2 font-mono text-black/70">{l.tool || l.method}</td>
                      <td className="px-3 py-2">{l.rw === 'write' ? <span className="text-amber-700">write</span> : <span className="text-sky-700">read</span>}</td>
                      <td className="px-3 py-2 text-black/60">{l.scope || '—'}</td>
                      <td className="px-3 py-2 text-black/50">{l.workspace_id ? wsName(l.workspace_id) : '—'}</td>
                      <td className="px-3 py-2"><Pill text={RESULT_LABEL[res] || res} cls={RESULT_CLS[res]} title={blockedTip} /></td>
                      <td className="px-3 py-2 text-black/50 whitespace-nowrap">{fmtLatency(l.latency_ms)}</td>
                      <td className="px-3 py-2"><CopyRequestId id={l.request_id} /></td>
                    </tr>); })}</tbody></table></div>)}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="docs" className="mt-4">
            <McpDocumentation />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create / result dialog */}
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeDialog())}>
        <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="mcp-create-dialog">
          {!created ? (<>
            <DialogHeader><DialogTitle>New MCP connection</DialogTitle><DialogDescription>Scope the connection to specific workspaces (or all, for Super Admin) and choose which integrations it may access.</DialogDescription></DialogHeader>
            <div className="space-y-4 py-1">
              <div><Label className="text-[12px] font-semibold text-black/70">Connection name</Label>
                <Input data-testid="mcp-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Acme ChatGPT" className="mt-1" /></div>

              <div><Label className="text-[12px] font-semibold text-black/70">Access scope</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}><SelectTrigger className="mt-1" data-testid="mcp-scope-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="selected">Selected workspaces</SelectItem><SelectItem value="global">Global (all workspaces)</SelectItem></SelectContent></Select></div>

              {form.scope === 'selected' && (
                <div><Label className="text-[12px] font-semibold text-black/70">Accessible Workspaces {form.workspace_ids.length > 0 && <span className="text-black/40 font-normal">({form.workspace_ids.length} selected)</span>}</Label>
                  {/* selected chips */}
                  {form.workspace_ids.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="mcp-ws-chips">
                      {form.workspace_ids.map((id) => (
                        <span key={id} data-testid={`ws-chip-${id}`} className="inline-flex items-center gap-1 rounded-full bg-[#0F62FE]/10 text-[#0F62FE] border border-[#0F62FE]/20 pl-2.5 pr-1 py-0.5 text-[12px] font-medium">
                          {wsName(id)}
                          <button onClick={() => toggleWs(id)} data-testid={`ws-chip-remove-${id}`} className="hover:bg-[#0F62FE]/20 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>)}
                  <div className="relative mt-2"><Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-black/30" /><Input data-testid="mcp-ws-search" value={wsSearch} onChange={(e) => setWsSearch(e.target.value)} placeholder="Search name, company, owner email or ID" className="pl-8" /></div>
                  <div className="mt-2 max-h-48 overflow-y-auto border border-black/10 rounded-lg divide-y divide-black/5" data-testid="mcp-ws-list">
                    {filteredWs.slice(0, 50).map((w) => (
                      <label key={w.id} className="flex items-start gap-2 p-2 cursor-pointer hover:bg-black/[0.02]" data-testid={`ws-option-${w.id}`}>
                        <input type="checkbox" className="mt-0.5" checked={form.workspace_ids.includes(w.id)} onChange={() => toggleWs(w.id)} />
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium text-[#0A0A0A]">{w.name}{w.company_name ? <span className="text-black/40 font-normal"> · {w.company_name}</span> : null}</div>
                          <div className="text-[11px] text-black/50">{w.owner_email || 'no owner'}</div>
                          <div className="text-[10px] text-black/30 font-mono">{w.id}</div>
                        </div>
                      </label>))}
                    {filteredWs.length === 0 && <div className="p-3 text-[12px] text-black/40">No matches</div>}
                  </div>
                </div>)}

              <div><Label className="text-[12px] font-semibold text-black/70">Available Integrations</Label>
                <p className="text-[11px] text-black/45 mt-0.5">Which integrations should this MCP connection be allowed to access? <span className="text-black/35">(none selected = all)</span></p>
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  {selectableSources.map((s) => (
                    <label key={s.key} className={`flex items-center gap-2 text-[12px] rounded border px-2 py-1.5 ${s.comingSoon ? 'opacity-60 cursor-not-allowed bg-slate-50' : 'cursor-pointer hover:bg-black/[0.02]'}`} data-testid={`source-${s.key}`}>
                      <input type="checkbox" disabled={s.comingSoon} checked={form.sources.includes(s.key)} onChange={() => !s.comingSoon && toggleSource(s.key)} />
                      <span className="flex-1">{s.label}</span>
                      {s.comingSoon ? <span className="text-[9px] text-slate-500 font-medium">Coming Soon</span> : <span className="text-[9px] text-emerald-600">✓</span>}
                    </label>))}
                </div></div>

              <div className="flex items-center justify-between rounded-lg border border-black/10 px-3 py-2">
                <div><div className="text-[13px] font-medium">Capabilities</div><div className="text-[11px] text-black/45">Read is always included</div></div>
                <label className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid="mcp-write-toggle"><input type="checkbox" checked={form.write} onChange={(e) => setForm({ ...form, write: e.target.checked })} /> Allow write</label>
              </div>

              <div><Label className="text-[12px] font-semibold text-black/70">Expiration (days)</Label>
                <Input data-testid="mcp-ttl-input" type="number" min={1} value={form.ttl_days} onChange={(e) => setForm({ ...form, ttl_days: e.target.value })} className="mt-1" /></div>

              {form.scope === 'global' && (<div className="flex items-start gap-2 text-[12px] text-purple-800 bg-purple-50 border border-purple-300 rounded-lg p-2.5" data-testid="global-warning"><ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" /> <span><strong>Global access.</strong> This credential can read across every Nova workspace. Share it only with trusted super admins.</span></div>)}
            </div>
            <DialogFooter><Button variant="outline" onClick={closeDialog}>Cancel</Button><Button data-testid="mcp-generate-btn" onClick={create} disabled={creating} className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white">{creating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <KeyRound className="w-4 h-4 mr-1.5" />} Generate MCP Connection</Button></DialogFooter>
          </>) : (<>
            <DialogHeader><DialogTitle>Connection ready</DialogTitle><DialogDescription>Copy the connector URL now — the credential is shown only once.</DialogDescription></DialogHeader>
            <div className="py-2 space-y-3">
              <div><Label className="text-[12px]">Connector URL (paste into ChatGPT)</Label>
                <div className="flex items-center gap-2 mt-1 rounded-lg border border-[#0F62FE]/30 bg-[#0F62FE]/5 p-2"><code data-testid="mcp-connector-url" className="font-mono text-[11px] break-all flex-1">{created.connector_url}</code><Button size="sm" variant="ghost" onClick={copyUrl} data-testid="mcp-copy-url" className="text-[#0F62FE]">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</Button></div></div>
              <div className="text-[12px] text-black/60 grid grid-cols-2 gap-2">
                <div><span className="text-black/40">Scope:</span> {created.connection.scope}</div>
                <div><span className="text-black/40">Accessible Workspaces:</span> {wsConcise(created.connection.allowed_workspace_ids)}</div>
                <div className="col-span-2"><span className="text-black/40">Available Integrations:</span> {(created.connection.sources && created.connection.sources.length) ? created.connection.sources.join(', ') : 'All'}</div>
                <div className="col-span-2"><span className="text-black/40">Capabilities:</span> <CapBadges caps={created.connection.permissions} /></div>
              </div>
            </div>
            <DialogFooter><Button data-testid="mcp-done-btn" onClick={closeDialog} className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white">Done</Button></DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* View details dialog */}
      <Dialog open={!!viewConn} onOpenChange={(o) => !o && setViewConn(null)}>
        <DialogContent className="sm:max-w-md" data-testid="mcp-view-dialog">
          {viewConn && (<>
            <DialogHeader><DialogTitle>{viewConn.name}</DialogTitle><DialogDescription>Connection details. The original token is never shown again.</DialogDescription></DialogHeader>
            <div className="py-1 text-[13px] space-y-2">
              {[['Masked token', <MaskedToken value={viewConn.masked_token} />],
                ['Scope', viewConn.scope],
                ['Accessible Workspaces', (viewConn.allowed_workspace_ids || []).length ? (viewConn.allowed_workspace_ids).map(wsName).join(', ') : 'All (global)'],
                ['Available Integrations', (viewConn.sources && viewConn.sources.length) ? viewConn.sources.join(', ') : 'All'],
                ['Capabilities', <CapBadges caps={viewConn.permissions} />],
                ['Status', <Pill text={viewConn.status} cls={STATUS_CLS[viewConn.status]} />],
                ['Created by', viewConn.created_by || '—'],
                ['Created', fmtFull(viewConn.created_at)],
                ['Last rotated', viewConn.rotated_at ? fmtFull(viewConn.rotated_at) : '—'],
                ['Last successful request', (() => { const t = lastSuccessFor(viewConn.id); return t ? fmtFull(t) : '—'; })()],
                ['Total requests', viewConn.request_count || 0],
                ['Expiration', viewConn.expires_at ? fmtFull(viewConn.expires_at) : '—']].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3"><span className="text-black/45">{k}</span><span className="text-right text-[#0A0A0A]">{v}</span></div>))}
            </div>
            {viewConn.status === 'active' && (<DialogFooter>
              <Button variant="outline" onClick={() => rotate(viewConn.id)} data-testid="view-rotate"><RotateCw className="w-4 h-4 mr-1.5" /> Rotate</Button>
              <Button onClick={() => revoke(viewConn.id)} data-testid="view-revoke" className="bg-red-600 hover:bg-red-700 text-white"><Ban className="w-4 h-4 mr-1.5" /> Revoke</Button>
            </DialogFooter>)}
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
